// Home's assessment card (M8-ASSESS):
// - anyone with a questionnaire pending (assessments.mine): "Your assessment is waiting", whose
//   button asks for a fresh private link (assessments.myLink) and goes to it;
// - managers: the latest open round's progress or, without one, the last report — each linking
//   to Setup › Assessments.
// Absent when there is nothing to show (no rounds, a refusal): nothing is a fake "0".
import { Badge, Button, HomeCard, HomeCardRow, Progress } from "@companyos/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ClipboardCheckIcon, ClipboardListIcon } from "lucide-react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { formatDateTime } from "../format";
import { problemText } from "../structure/parts";
import { progressLine, progressPercent, QUESTIONNAIRE_LABEL } from "./assess-model";
import { homeRoundsQuery, mineQuery } from "./assess-queries";

export function AssessHomeCard({
  api,
  company,
  manager,
}: {
  api: Api;
  company: CompanyAccess;
  manager: boolean;
}) {
  const mine = useQuery(mineQuery(api, company.id));
  const rounds = useQuery({ ...homeRoundsQuery(api, company.id), enabled: manager });
  const navigate = useNavigate();
  const open = useMutation({
    mutationFn: (roundId: string) =>
      api.client.assessments.myLink({ params: { co: company.id, id: roundId } }),
    onSuccess: (link) => void navigate({ to: link.path } as never),
  });
  const pending = mine.data ?? [];
  const list = rounds.data?.data ?? [];
  const live = list.find((r) => r.state === "open");
  const reported = list.find((r) => r.state === "reported");
  const round = live ?? reported;
  if (pending.length === 0 && !round) return null;
  return (
    <HomeCard
      title="Assessments"
      flush
      data-slot="home-assessments"
      {...(manager
        ? {
            action: (
              <Link
                to="/c/$slug/setup/assessments"
                params={{ slug: company.slug }}
                className="text-xs text-primary-text hover:underline"
              >
                All rounds
              </Link>
            ),
          }
        : {})}
    >
      {pending.map((m) => (
        <HomeCardRow
          key={m.participant_id}
          data-mine={m.round_id}
          lead={<ClipboardListIcon className="size-4 text-primary-text" aria-hidden={true} />}
          title={<span className="truncate font-medium">Your assessment is waiting</span>}
          description={
            open.isError && open.variables === m.round_id
              ? problemText(open.error)
              : `${m.round_name} · about ${m.minutes} min${m.expires_at ? ` · until ${formatDateTime(m.expires_at)}` : ""}`
          }
          trailing={
            <Button size="sm" disabled={open.isPending} onClick={() => open.mutate(m.round_id)}>
              {m.state === "in_progress" ? "Continue" : "Start"}
            </Button>
          }
        />
      ))}
      {manager && round ? (
        <HomeCardRow
          data-round={round.id}
          lead={<ClipboardCheckIcon className="size-4 text-muted-foreground" aria-hidden={true} />}
          title={
            <Link
              to="/c/$slug/setup/assessments"
              params={{ slug: company.slug }}
              search={{ round: round.id, view: live ? "progress" : "report" } as never}
              className="truncate font-medium hover:underline"
            >
              {round.name}
            </Link>
          }
          description={
            live
              ? `${QUESTIONNAIRE_LABEL[round.questionnaire]} · ${progressLine(round)}`
              : `Report version ${round.report_version} · ${progressLine(round)}`
          }
          trailing={
            live ? (
              <Progress value={progressPercent(round)} className="w-20" aria-label="Responded" />
            ) : (
              <Badge variant="success" dot>
                Report ready
              </Badge>
            )
          }
        />
      ) : null}
    </HomeCard>
  );
}
